export const meta = {
  id: '16-dative',
  order: 16,
  titleFi: 'Datiivi',
  titleEn: 'Dative',
  duolingoUnit: 'duo-18-dative',
  // rhaella, sansa: the family question pairs of this week's speaking (together/speaking: rhaella-issa, suku); with the
  // rising pace (2026-10-14) the quotas would bring them only in lesson 21
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['āeksio', 'rūklon', 'tepagon', 'irughagon', 'gōvilemagon', 'va', 'hen', 'lentot', 'jagon', 'oktion', 'kasta', 'adere', 'gevie', 'rhaella', 'sansa', 'ours:tembiapos', 'ours:vūjinon', 'ours:kāvrurlion', 'ours:elēnurlion', 'ours:kelilion', 'ours:sōvelion', 'ours:jikilla', 'ours:umbītsos', 'ours:havtys', 'ours:sennon', 'ours:ēngenky', 'ours:sȳremion', 'ours:rāenābnon', 'ours:rhaennon', 'ours:zgiēñemion', 'ours:zgiēñemilaksy', 'ours:irughilaksir', 'ours:mīrēbnon', 'ours:hidañurilla', 'ours:aderēdir', 'ours:brāediapos', 'ours:rytsāvurlion', 'ours:euri', 'ours:sinema', 'ours:pikniki', 'ours:dollari', 'ours:pundi', 'ours:bardulāzma', 'ours:sirkusi', 'ours:krediti', 'ours:faranki'],
  exampleIds: ['ex-17d0e4dba360', 'hotd-s3e01-002', 'ex-7b6eb4f655d8', 'ex-238f4af2b1bf', 'ex-89cf74999442', 'ex-bd5f072ef7c4'],
  checkFocus: { cases: ['dat'] },   // what the end-of-lesson check tests first
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Dative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Dative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Prepositions',
    'https://wiki.languageinvention.com/index.php?title=Lentot',
    'https://wiki.languageinvention.com/index.php?title=Kasta',
    'https://wiki.languageinvention.com/index.php?title=Gōvilemagon',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['cases', 'adj-position', 'valence'],
  drills: { cases: ['dat'] },
  showLines: ['hotd-s3e01-002'],
  pronunciation: { focus: 'diphthongs', words: ['āeksio', 'oktion', 'gevie', 'aerikagon', 'kerio'] },
  letter: {
    fi: 'Kerro, mitä annat kenellekin ja minne menet tällä viikolla (datiivi).',
    en: 'Write what you give to whom and where you are going this week (dative).',
  },
  scene: 'hotd-s3e01-1',
}

# Datiivi

Tähän asti lauseissa on ollut tekijä ja kohde: kuka tekee ja mihin teko kohdistuu. Nyt mukaan tulee kolmas osallistuja: se, **jolle** jotakin annetaan, tai paikka, **jonne** mennään. Kumpaakin varten korkeavalyriassa on datiivi. Se on ensimmäinen niin sanotuista ei-ydinsijoista, ja sen mukana tulee myös uusi, tärkeä adjektiivisääntö.

## Kenelle? Datiivi vastaanottajana

Datiivi merkitsee lauseen epäsuoraa objektia. Tyypillisin epäsuora objekti on vastaanottaja: *tyttö antoi kukan pojalle*. Suomessa tähän tehtävään on allatiivi, ja vastaavuus on niin suora, että sitä voi käyttää muistisääntönä:

<Compare fi="poja|lle" hv="taob|ot" label="allatiivi ~ datiivi: vastaanottaja" />

Duolingon Tips & Notesin perusesimerkki on tämä:

<Interlinear hv="Taob|ot rūkl|on tep|an." gloss="poika-DAT kukka-ACC antaa-PRS.1SG" fi="Annan pojalle kukan." />

Datiivi tulee yleensä subjektin ja objektin väliin, mutta järjestys ei ole ehdoton. Myös <Hv fi="Annan pojalle kukan.">Rūklon taobot tepan.</Hv> on aivan tavallinen lause. Suomalaiselle tämä on tuttua: sijapääte kertoo sanan tehtävän, joten sanajärjestys saa joustaa.

Verbi <Hv glossed>tepagon</Hv> ’antaa’ on datiivin luonteva pari – sanakirjan määritelmäkin kuuluu ”antaa jotakin (akkusatiivi) jollekulle (datiivi)”. Näin se toimii sarjan repliikissä:

<Example id="ex-17d0e4dba360" />

<Hv glossed>Aōt</Hv> on sinä-pronominin datiivi ’sinulle’. Persoonapronominien taivutukseen palaamme pian niiden omalla oppitunnilla.

<Note>
**Peterson:** <Hv glossed>tepagon</Hv> on ’antaa’ silloin, kun antaja tai saaja on minä, me, sinä tai te. Muuten ’antaa’ on <Hv glossed>irughagon</Hv>: <Hv fi="Poika antaa tytölle kukan.">Taoba riñot rūklon irughas.</Hv>
</Note>

## Minne? Datiivi määränpäänä

Korkeavalyriassa datiivi ilmaisee myös määränpään, kun verbi itse kertoo liikkeestä. <Hv glossed>Lentot jān</Hv> on ’menen taloon, menen kotiin’; <Hv glossed>lentot</Hv> on tässä sanan <Hv glossed>lenton</Hv> ’talo, koti’ datiivi. Suomessa määränpää on illatiivissa tai allatiivissa (*taloon, talolle*), valyriassa kumpikin ilmaistaan datiivilla.

Lohikäärmeratsastajat käyttävät tätä muotoa sellaisenaan käskynä: pelkkä <Hv glossed>Lentot!</Hv> tarkoittaa lohikäärmeelle ’Kotiin!’. Repliikissä on myös toinen määränpään datiivi, <Hv glossed>Zaldrīzdōrot</Hv> ’Lohikäärmekivelle’:

<Line id="hotd-s3e01-002" />

<Note>
Sanakirjassa <Hv glossed>lentot</Hv> on myös oma hakusanansa, adjektiivi ’sisäinen, koti-, sisällis-’ (Hizdahr puhuu sisällissodasta, <Hv glossed>lentot vīlībāzme</Hv>). Wikissä samalla sivulla on siis kolme samannäköistä sanaa: tämä adjektiivi, lohikäärmekäsky ’Kotiin!’ ja sanan <Hv l="lenton">lenton</Hv> datiivi. Tällä oppitunnilla olennainen on datiivi.
</Note>

## Näin datiivi muodostetaan

Datiivi on helppo, kun genetiivi on jo hallussa: **yksikön datiivi on lähes aina genetiivi + <Hv>-t</Hv>**.

- <Hv>vala</Hv> → genetiivi <Hv>valo</Hv> → datiivi <Hv glossed>valot</Hv> ’miehelle’
- <Hv l="lenton">lenton</Hv> → <Hv l="lenton">lento</Hv> → <Hv glossed>lentot</Hv> ’taloon, talolle’

Jos genetiivin pääte on pitkä <Hv>-ō</Hv>, vokaali lyhenee <Hv>t</Hv>:n edellä:

- <Hv glossed>āeksio</Hv> ’isäntä, herra’ → <Hv>āeksiō</Hv> → <Hv glossed>āeksiot</Hv> ’isännälle’

**Monikon datiivi on aina sama kuin monikon genetiivi.** Kahden muodon sijaan opettelet siis yhden.

<Paradigm lemma="āeksio" cases="nom,acc,gen,dat" numbers="sg,pl" />

Suomessakin genetiivi ja allatiivi rakentuvat samalle vartalolle: *miehe-n*, *miehe-lle*; *talo-n*, *talo-lle*. Kun osaat sanan genetiivin, osaat jo puolet datiivista. Ajatus on suomalaiselle tuttu.

<Pronounce l="āeksio" word="āeksio" />

## Datiivia vaativia verbejä

Jotkin verbit vaativat datiiviobjektin, vaikka suomessa sama asia ilmaistaisiin aivan toisin. Tämän oppitunnin esimerkki on <Hv glossed>gōvilemagon</Hv> ’panna jonkin alle’: se, **jonka alle** jotakin pannaan, on datiivissa. Suomeksi sanoisimme *pöydän alle* (genetiivi ja postpositio), mutta valyriassa riittää yksi sijamuoto.

<Note>
Tarkemmin sanottuna: Duolingon ohjeiden mukaan objektin pitäisi oikeastaan olla genetiivissä, kun se on suoraan verbin edellä. Sanakirja tarkentaa, että genetiivi on mahdollinen yksikössä. Ohjeet lisäävät, että toistaiseksi datiivi riittää. Tässä esimerkkilauseessa on genetiivivaihtoehto – <Hv glossed>qurdo</Hv> on sanan <Hv glossed>qurdon</Hv> ’pöytä’ genetiivi:

<Example id="ex-bd5f072ef7c4" />
</Note>

## Prepositiot va ja hen

Korkeavalyriassa on vain kolme prepositiota, ja niistä kaksi tulee nyt vastaan: <Hv>va</Hv> ja <Hv>hen</Hv>. Niitä voi käyttää eri sijojen kanssa, ja merkitys riippuu sijasta. Tällä oppitunnilla ne ovat datiivin kanssa.

**<Hv glossed>va</Hv> + datiivi** tarkoittaa ’sisään, jonnekin asti, kohti, ylös jonkin luo’. Pelkkä datiivi kertoo määränpään, <Hv>va</Hv> tarkentaa sitä. Duolingon ohjeiden vastakohtapari:

- <Hv glossed>Lentot jān.</Hv> ’Menen taloon / talolle.’
- <Hv glossed>Va lentot jān.</Hv> ’Menen sisään taloon.’

Toisenlaisessa yhteydessä jälkimmäinen voi tarkoittaa myös ’menen talon luo’ tai ’menen taloa kohti’. Suomi tekee tämän eron sijoilla (*taloon* ~ *talolle* ~ *talon luo*), valyria prepositiolla.

<Compare glossed fi="talo|on" hv="va lent|ot" label="illatiivi ~ va + datiivi" />

<Example id="ex-7b6eb4f655d8" />

**<Hv glossed>hen</Hv> + datiivi** tarkoittaa joko ’ulos jonkin sisältä’ tai ’jonkin takia, jonkin vuoksi’. Tärkeä tarkennus: <Hv>hen</Hv> + datiivi ei sovi kaupungista lähtemiseen, mutta huoneesta poistumiseen se sopii. Kyse on nimenomaan sisältä ulos tulemisesta.

- <Hv glossed>Hen lentot jān.</Hv> ’Menen ulos talosta.’
- <Hv glossed>Hen dāriot oktiot jān.</Hv> ’Menen kaupunkiin kuningattaren vuoksi.’

Duolingon ohjeiden mukaan jälkimmäinen käyttö on jopa yleisempi. Suomeksi ensimmäinen on elatiivi (*talosta*), jälkimmäinen *vuoksi*- tai *takia*-rakenne.

<Compare glossed fi="talo|sta" hv="hen lent|ot" label="elatiivi ~ hen + datiivi" />

<Example id="ex-89cf74999442" />

Rhaenyran repliikissä on kumpikin prepositio samassa lauseessa – talosta taloon:

<Example id="ex-238f4af2b1bf" />

## Adjektiivit datiivissa: paikka ratkaisee

Nyt tärkein uusi asia. Jo aurinkosuvun monikon nominatiivissa näit, että adjektiivin muoto voi riippua siitä, onko se substantiivin edellä vai perässä. Datiivista alkaen sama koskee monia ei-ydinsijojen muotoja. Sääntö on tämä:

- **jälkiasemassa** (substantiivin perässä) adjektiivi on **pidempi**,
- **etuasemassa** (substantiivin edellä) se on **lyhyempi**.

Suomesta on apua vain puoliksi. Suomen adjektiivi mukautuu sijassa aivan kuten valyrian: *isolle talolle*. Suomessa määriteadjektiivi on kuitenkin lähes aina substantiivin edellä, eikä sen muoto riipu paikasta. Tämä on siis uutta, ja se kannattaa opetella taulukoista.

Mallisanoina ovat kunkin adjektiiviluokan edustajat: <Hv glossed>kasta</Hv> ’sininen, vihreä’ (<Term id="adjektiiviluokka">I-luokka</Term>), <Hv glossed>adere</Hv> ’nopea, liukas’ (<Term id="adjektiiviluokka">II-luokka</Term>) ja <Hv glossed>gevie</Hv> ’kaunis’ (<Term id="adjektiiviluokka">III-luokka</Term>). Ensin jälkiasema:

<Paradigm lemma="kasta" filter="positive.post" cases="dat" numbers="sg,pl" />

<Paradigm lemma="adere" filter="positive.post" cases="dat" numbers="sg,pl" />

<Paradigm lemma="gevie" filter="positive.post" cases="dat" numbers="sg,pl" />

Sitten etuasema. Katso, miten loppu lyhenee: <Hv l="kasta">kastot</Hv> → <Hv l="kasta">kasto</Hv>, <Hv>aderot</Hv> → <Hv>adero</Hv>, <Hv>geviot</Hv> → <Hv>gevio</Hv>, <Hv>aderȳrot</Hv> → <Hv>aderȳr</Hv>.

<Paradigm lemma="kasta" filter="positive.pre" cases="dat" numbers="sg,pl" />

<Paradigm lemma="adere" filter="positive.pre" cases="dat" numbers="sg,pl" />

<Paradigm lemma="gevie" filter="positive.pre" cases="dat" numbers="sg,pl" />

**Yksi tärkeä poikkeus.** Jos etuasemaisen adjektiivin jälkeen tuleva sana alkaa vokaalilla, ne muodot, jotka jälkiasemassa päättyvät pelkkään <Hv>t</Hv>:hen, säilyttävät <Hv>t</Hv>:nsä. <Hv>-ȳrot</Hv>-loppuiset muodot eivät kuulu tähän. Käytännössä poikkeus koskee vain yksikköä, ja <Term id="adjektiiviluokka">II- ja III-luokassa</Term> vain kuu- ja aurinkosukua. Siksi taulukoissa on etuasemassa kaksi muotoa, esimerkiksi <Hv l="kasta">kasto</Hv> ja <Hv l="kasta">kastot</Hv>: jälkimmäistä käytetään vokaalin edellä.

Tähän suomesta ei löydy mallia. Paras keino on sanoa parit ääneen: <Hv fi="kauniille tytölle">gevio riñot</Hv> mutta <Hv fi="kauniille herralle">geviot āeksiot</Hv>. Vokaalin edellä <Hv>t</Hv> ikään kuin sitoo sanat yhteen.

<Note>
<Hv>Kasta</Hv> on Petersonin mukaan ensimmäinen korkeavalyrian adjektiivi, jonka hän loi. Hän halusi ensimmäisestä adjektiivista merkitykseltään ja muodoltaan suoraviivaisen, jotta mukautumisen voisi näyttää selvästi. Siksi se on yhä hyvä mallisana.
</Note>

<Pronounce word="gōvilemagon" />

<Pronounce word="gevie" />

## Harjoituslauseita

Nämä lauseet on koottu tämän oppitunnin säännöillä ja taivutustaulukoiden muodoilla. Ne eivät ole Petersonin lauseita.

<Practice hv="Ābrot rūklon tepan." fi="Annan naiselle kukan." />

<Practice hv="Azantys ynot rūklon tepas." fi="Ritari antaa minulle kukan." />

<Practice hv="Va oktiot jān." fi="Menen sisään kaupunkiin." />

<Practice hv="Taoba gevio riñot rūklon irughas." fi="Poika antaa kauniille tytölle kukan." />

<Practice hv="Riña geviot āeksiot rūklon irughas." fi="Tyttö antaa kauniille herralle kukan." />

Kahdessa viimeisessä lauseessa on sama etuasemainen adjektiivi. Ensimmäisessä seuraava sana alkaa konsonantilla (<Hv l="riña">riñot</Hv>), joten muoto on lyhyt <Hv>gevio</Hv>. Toisessa se alkaa vokaalilla (<Hv l="āeksio">āeksiot</Hv>), joten <Hv>t</Hv> säilyy: <Hv>geviot</Hv>.

## Sanat

<Vocab />

<Check />
